import { useEffect, useState, type ReactNode } from 'react';
import { X, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';

export interface AIAssistantField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number';
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  defaultValue?: string;
}

interface AIAssistantShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  fields: AIAssistantField[];
  onGenerate: (values: Record<string, string>) => Promise<void>;
  generating: boolean;
  error?: string | null;
  result?: ReactNode;
  applyLabel?: string;
  onApply?: () => void;
  applying?: boolean;
}

function initialValuesFromFields(fields: AIAssistantField[]): Record<string, string> {
  const init: Record<string, string> = {};
  for (const f of fields) {
    init[f.key] = f.defaultValue ?? '';
  }
  return init;
}

export default function AIAssistantShell({
  open,
  onClose,
  title,
  description,
  fields,
  onGenerate,
  generating,
  error,
  result,
  applyLabel = 'Apply to form',
  onApply,
  applying,
}: AIAssistantShellProps) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialValuesFromFields(fields)
  );

  useEffect(() => {
    if (open) {
      setValues(initialValuesFromFields(fields));
    }
  }, [open, fields]);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div
        className="relative flex h-full w-full max-w-lg flex-col glass-card !rounded-none animate-slide-in-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-assistant-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-glass bg-accent2/15 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-accent2/20 border border-accent2/30 p-2">
              <Sparkles className="h-5 w-5 text-accent2" aria-hidden />
            </div>
            <div>
              <h2 id="ai-assistant-title" className="text-lg font-semibold tracking-tightest text-glass-text">
                {title}
              </h2>
              {description && <p className="text-sm text-glass-text-2">{description}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-glass-text-3 transition hover:bg-glass-3 hover:text-glass-text"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto glass-scroll p-6">
          {fields.map((field) => (
            <div key={field.key}>
              <label className="mb-1 block text-sm font-medium text-glass-text-2">
                {field.label}
                {field.required && <span className="ml-1 text-rose-500 dark:text-rose-300">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  className="w-full rounded-lg border border-glass bg-glass-2 text-glass-text px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  rows={4}
                  placeholder={field.placeholder}
                  value={values[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              ) : field.type === 'select' ? (
                <select
                  className="w-full rounded-lg border border-glass bg-glass-2 text-glass-text px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  value={values[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                >
                  <option value="">Select...</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'number' ? (
                <input
                  type="number"
                  className="w-full rounded-lg border border-glass bg-glass-2 text-glass-text px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  placeholder={field.placeholder}
                  value={values[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              ) : (
                <input
                  type="text"
                  className="w-full rounded-lg border border-glass bg-glass-2 text-glass-text px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  placeholder={field.placeholder}
                  value={values[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              )}
            </div>
          ))}

          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-500 dark:text-rose-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="mt-4 border-t border-glass pt-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-glass-text">
                <Sparkles className="h-4 w-4 text-accent2" aria-hidden />
                AI Output
              </h3>
              <div className="rounded-xl border border-accent2/30 bg-accent2/10 p-4">{result}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-glass bg-glass-2 px-6 py-4">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => void onGenerate(values)}
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" aria-hidden />
                  Generate
                </>
              )}
            </Button>
            {result && onApply && (
              <Button type="button" onClick={onApply} disabled={applying}>
                {applying ? 'Applying...' : applyLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
